import { useEffect, useState } from "react";
import type { DealDetail, DealNote } from "@edge/shared";
import { fetchDealDetail, fetchDealNotes } from "../api";

interface FocusData {
  detail: DealDetail | null;
  notes: DealNote[];
  loading: boolean;
  error: string | null;
  /** Refetch detail + notes for the currently-focused deal. */
  reload: () => void;
}

/**
 * Loads the deal detail + Zoho notes for the focused deal. Re-fetches when
 * the dealId changes. Used by FocusView.
 */
export function useFocusData(dealId: string | null): FocusData {
  const [detail, setDetail] = useState<DealDetail | null>(null);
  const [notes, setNotes] = useState<DealNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!dealId) {
      setDetail(null);
      setNotes([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([fetchDealDetail(dealId), fetchDealNotes(dealId)]).then(
      ([detailR, notesR]) => {
        if (cancelled) return;
        if (detailR.status === "fulfilled") setDetail(detailR.value);
        else {
          setError("deal_load_failed");
          console.error("[edge] focus detail fetch failed", detailR.reason);
        }
        if (notesR.status === "fulfilled") setNotes(notesR.value);
        else console.error("[edge] focus notes fetch failed", notesR.reason);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [dealId, reloadKey]);

  return {
    detail,
    notes,
    loading,
    error,
    reload: () => setReloadKey((k) => k + 1),
  };
}
